/**
 * MicButton — large central session control for voice mode.
 *
 * iOS gesture chain (Pitfall C): on FIRST tap, shows IosVoiceWarning if applicable,
 * then calls onStart (which must call resumeAudioContext() before mic start).
 *
 * Visual states (one large circular control, mobile-app interaction model):
 *   - processing (gray, spinner): turn sent, awaiting AI
 *   - aiSpeaking (amber, stop icon, gentle pulse): tap to interrupt AI audio
 *   - listening (pulsing red, stop icon): tap to finish your answer
 *   - idle (stone, mic icon): tap to start speaking
 *   - disabled (gray): connecting / paused / read-only
 */

import { useRef, useState } from 'react';
import { IosVoiceWarning, isIosSafari, isIosWarningDismissed } from '../Onboarding/IosVoiceWarning';

interface MicButtonProps {
  /** Called when user taps to start recording (must call resumeAudioContext inside) */
  onStart: () => Promise<void>;
  /** Called when user taps to stop recording */
  onStop: () => Promise<void>;
  /** Called when user taps while the AI is speaking — stops AI audio playback */
  onInterrupt?: () => void;
  /** True while Transcribe is actively listening */
  isListening: boolean;
  /** True while the user's turn is being processed by the AI */
  processing?: boolean;
  /** True while the AI is responding (streaming text or playing audio) */
  aiSpeaking?: boolean;
  /** True when button should be fully disabled (connecting, paused, read-only) */
  disabled: boolean;
  /** Navigate back to mode selection */
  onSwitchToText?: () => void;
}

export function MicButton({ onStart, onStop, onInterrupt, isListening, processing = false, aiSpeaking = false, disabled, onSwitchToText }: MicButtonProps) {
  const [showIosWarning, setShowIosWarning] = useState(false);
  const handledTouchRef = useRef(false);

  const isInactive = disabled || processing;

  const handleClick = async () => {
    if (isInactive) return;

    if (isListening) {
      await onStop();
      return;
    }

    if (aiSpeaking) {
      onInterrupt?.();
      return;
    }

    // iOS Safari: show warning once if not yet dismissed
    if (isIosSafari() && !isIosWarningDismissed()) {
      setShowIosWarning(true);
      return;
    }

    await onStart();
  };

  const handleIosContinue = async () => {
    setShowIosWarning(false);
    await onStart();
  };

  const handleIosUseText = () => {
    setShowIosWarning(false);
    onSwitchToText?.();
  };

  const ariaLabel = isListening
    ? 'Stop speaking'
    : aiSpeaking
    ? 'Stop AI audio and take your turn'
    : processing
    ? 'Processing your answer'
    : 'Start speaking';

  return (
    <>
      {showIosWarning && (
        <IosVoiceWarning
          onContinueVoice={handleIosContinue}
          onUseText={handleIosUseText}
        />
      )}

      <button
        type="button"
        onClick={async () => {
          if (handledTouchRef.current) {
            handledTouchRef.current = false;
            return;
          }
          await handleClick();
        }}
        onTouchStart={async (e) => {
          e.preventDefault();
          handledTouchRef.current = true;
          await handleClick();
        }}
        disabled={isInactive}
        aria-label={ariaLabel}
        aria-pressed={isListening}
        className={[
          'relative w-24 h-24 rounded-full flex items-center justify-center',
          'transition-all duration-200 focus:outline-none focus:ring-4',
          isInactive
            ? 'bg-stone-200 text-stone-400 cursor-not-allowed focus:ring-stone-200'
            : isListening
            ? 'bg-red-500 text-white shadow-lg focus:ring-red-300'
            : aiSpeaking
            ? 'bg-amber-500 text-white shadow-lg hover:bg-amber-600 focus:ring-amber-300'
            : 'bg-stone-800 text-white hover:bg-stone-700 shadow-md focus:ring-stone-400',
        ].join(' ')}
      >
        {/* Pulsing ring when listening */}
        {isListening && (
          <span
            className="absolute inset-0 rounded-full bg-red-400 opacity-50 animate-ping"
            aria-hidden="true"
          />
        )}

        {/* Gentle pulse while the AI is speaking */}
        {aiSpeaking && !isInactive && (
          <span
            className="absolute inset-0 rounded-full bg-amber-300 opacity-40 animate-pulse"
            aria-hidden="true"
          />
        )}

        {processing ? (
          // Spinner while the turn is processing
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            className="w-10 h-10 relative z-10 animate-spin"
            aria-hidden="true"
          >
            <path strokeLinecap="round" d="M12 3a9 9 0 109 9" />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-10 h-10 relative z-10"
            aria-hidden="true"
          >
            {isListening || aiSpeaking ? (
              // Stop icon — finish my answer / interrupt AI audio
              <path
                fillRule="evenodd"
                d="M4.5 7.5a3 3 0 013-3h9a3 3 0 013 3v9a3 3 0 01-3 3h-9a3 3 0 01-3-3v-9z"
                clipRule="evenodd"
              />
            ) : (
              // Mic icon when idle
              <>
                <path d="M8.25 4.5a3.75 3.75 0 117.5 0v8.25a3.75 3.75 0 11-7.5 0V4.5z" />
                <path d="M6 10.5a.75.75 0 01.75.75v1.5a5.25 5.25 0 1010.5 0v-1.5a.75.75 0 011.5 0v1.5a6.751 6.751 0 01-6 6.709v2.291h3a.75.75 0 010 1.5h-7.5a.75.75 0 010-1.5h3v-2.291a6.751 6.751 0 01-6-6.709v-1.5A.75.75 0 016 10.5z" />
              </>
            )}
          </svg>
        )}
      </button>
    </>
  );
}
